import { type PageEvent } from '@angular/material/paginator';
import { type Sort } from '@angular/material/sort';
import {
  BaseProps,
  shareReplayCold,
  snapshot,
} from '@principle-theorem/shared';
import {
  BehaviorSubject,
  ReplaySubject,
  combineLatest,
  type Observable,
} from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { type ObservableDataTable } from './observable-data-table';

interface ISort {
  active: string;
  direction: 'asc' | 'desc';
}

export class PaginatedPostgresTable<T extends object> {
  pageChange$ = new ReplaySubject<PageEvent>(1);
  sort$ = new BehaviorSubject<ISort>({
    active: 'uid',
    direction: 'asc',
  });
  pageSize$: Observable<number>;
  pageIndex$: Observable<number>;
  dataTable: ObservableDataTable<T>;
  loading$ = new BehaviorSubject<boolean>(false);

  constructor(pageSize: number) {
    this.pageSize$ = this.pageChange$.pipe(
      map((pageChange) => pageChange.pageSize)
    );

    this.pageIndex$ = this.pageChange$.pipe(
      map((pageChange) => pageChange.pageIndex)
    );

    this.pageChange$.next({
      pageIndex: 0,
      pageSize,
      length: 0,
    });
  }

  destroy(): void {
    this.dataTable.ngOnDestroy();
  }

  async sortChange(sort: Sort): Promise<void> {
    const pageChange = await snapshot(this.pageChange$);
    this.pageChange$.next({
      ...pageChange,
      pageIndex: 0,
    });
    this.sort$.next({
      active: sort.active,
      direction: sort.direction || 'asc',
    });
  }

  updatePage(page: PageEvent): void {
    this.pageChange$.next(page);
  }

  getRecords$<Props extends BaseProps>(
    queryFn: (vars: Props) => Promise<T[]>,
    queryProps: Props
  ): Observable<T[]> {
    return combineLatest([
      this.pageChange$.pipe(startWith(undefined)),
      this.sort$,
    ]).pipe(
      tap(() => this.loading$.next(true)),
      switchMap(([pageIndex, sort]) => {
        const limit = pageIndex?.pageSize || this.dataTable.pageSize;

        return queryFn({
          ...queryProps,
          offset: (pageIndex?.pageIndex || 0) * limit,
          limit,
          orderBy: {
            key: sort.active,
            direction: sort.direction,
          },
        });
      }),
      tap(() => this.loading$.next(false)),
      startWith([]),
      shareReplayCold()
    );
  }
}
